import {useCallback, useSyncExternalStore} from "react";
import {bffApi} from "../features/chat/bffApi";
import {HasEditorPrivileges, LocalStorageKeys} from "../types";

/**
 * Atomic feature bits. Each bit gates exactly one behavior, and every
 * consumer fork checks the single bit it represents via useFeature — never
 * a bundle. Bit values are persisted in localStorage: never renumber a live
 * bit. Use bits 0–30 only — JS bitwise ops truncate to 32-bit signed ints,
 * so 1 << 31 goes negative and 1 << 32 silently wraps to bit 0; past 31
 * concurrent features, migrate storage to BigInt or a key set. A deleted
 * feature's bit value may be reused after a rest period: unknown bits are
 * stripped on read/write (ALL_FEATURES_MASK), so a stale mask can only
 * resurrect a reused bit in a browser that never loaded the app between the
 * old feature's deletion and the new one's introduction.
 *
 * Bits 0 (NewThreadUi), 1 (PostSearch) and 4 (DashboardFrontpage) were retired
 * 2026-06 when those behaviors became unconditional; their values are held
 * vacant per the bit-reuse note above.
 */
export enum Feature {
    /** Rewrite app.dib.no / legacy file links to open in this app. */
    RewriteDibLinks = 1 << 2, // 4
    /** Allow viewing app.dib.no content in this app (ContentPage). */
    ViewDibContent = 1 << 3, // 8
    /** Internal-only tools: context sidebar, considered-sources button. */
    InternalFeatures = 1 << 5, // 32
    /** Dashed outline on gated elements. Annotates, never changes behavior. */
    DebugIndicators = 1 << 6, // 64
    /** New content-page design. Off (default) = legacy view. */
    NewContentDesign = 1 << 7, // 128
}

/** Who may have each feature on. Per-feature so requirements can diverge. */
const FEATURE_ROLES: Record<Feature, readonly number[]> = {
    [Feature.RewriteDibLinks]: HasEditorPrivileges,
    [Feature.ViewDibContent]: HasEditorPrivileges,
    [Feature.InternalFeatures]: HasEditorPrivileges,
    [Feature.DebugIndicators]: HasEditorPrivileges,
    [Feature.NewContentDesign]: HasEditorPrivileges,
};

const ALL_FEATURES = Object.values(Feature).filter(
    (value): value is Feature => typeof value === "number"
);

/** Union of all currently defined feature bits. Reads and writes strip
 *  anything outside this mask, so a deleted feature's stale bit never
 *  reaches a consumer and falls out of storage on the next write. */
const ALL_FEATURES_MASK = ALL_FEATURES.reduce((acc, bit) => acc | bit, 0);

/**
 * UI layer only: each settings-page toggle flips every bit in its mask at
 * once. Consumers never reference bundles, so re-bundling never touches
 * consumer code or stored values. When re-bundling, re-check that every bit
 * in a mask carries identical FEATURE_ROLES — a toggle is rendered only when
 * the user may toggle all of its bits.
 *
 * Display order and visual grouping are owned by SettingsPage; this registry
 * only defines what each toggle means. Labels resolve as
 * settings.feature.<key>.label / .description with the defaults below as
 * fallback until the i18n keys are registered.
 */
export const FEATURE_BUNDLES = {
    dibContentInApp: {
        mask: Feature.RewriteDibLinks | Feature.ViewDibContent,
        defaultLabel: "KAI Content View",
        defaultDescription:
            "Rewrite app.dib.no links to open in this app, and render that content here",
    },
    newContentDesign: {
        mask: Feature.NewContentDesign,
        defaultLabel: "Dev Mode Content Design",
        defaultDescription:
            "Render content pages with the new design instead of the legacy view",
    },
    internalFeatures: {
        mask: Feature.InternalFeatures,
        defaultLabel: "Internal Features",
        defaultDescription:
            "Show internal-only tools: context sidebar, considered sources",
    },
    debugIndicators: {
        mask: Feature.DebugIndicators,
        defaultLabel: "Debug Indicators",
        defaultDescription: "Outline feature-gated elements (visual only)",
    },
} as const;

export type FeatureBundleKey = keyof typeof FEATURE_BUNDLES;

/*
 * Persistence for the feature bitmask: one integer per user, stored under
 * `featureFlags:<brukerKey>`. localStorage is per-browser and shared between
 * accounts, so the key is scoped by user identity to keep flags from leaking
 * between accounts.
 *
 * When nothing is stored for the user yet, reads fall back to a mask derived
 * from the legacy flag keys, so the first resolved render is already correct.
 * FeatureFlagsMigration persists that derived value and removes the legacy
 * keys afterwards.
 */

const FEATURE_FLAGS_EVENT = "feature-flags-change";

// Legacy per-browser flag keys, readable only by the migration fallback
// below. Values were written by Mantine's useLocalStorage (JSON-serialized).
const LEGACY_KEYS = {
    experimentalMode: "experimentalMode",
    showInternalFeatures: "showInternalFeatures",
    showDebugIndicators: "showDebugIndicators",
    legacyMode: "legacyMode",
} as const;

function featureFlagsKey(brukerKey: string): string {
    return `${LocalStorageKeys.FeatureFlags}:${brukerKey}`;
}

function getItem(key: string): string | null {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function readLegacyFlag(key: string): boolean | undefined {
    const raw = getItem(key);
    if (raw === null) return undefined;
    try {
        const parsed = JSON.parse(raw);
        return typeof parsed === "boolean" ? parsed : undefined;
    } catch {
        return undefined;
    }
}

function deriveMaskFromLegacyFlags(): number {
    let mask = 0;
    // experimentalMode bundled the KAI content-view bits; the now-unconditional
    // thread/post-search/dashboard bits were retired and are no longer seeded.
    if (readLegacyFlag(LEGACY_KEYS.experimentalMode) === true) {
        mask |= Feature.RewriteDibLinks | Feature.ViewDibContent;
    }
    if (readLegacyFlag(LEGACY_KEYS.showInternalFeatures) === true) {
        mask |= Feature.InternalFeatures;
    }
    if (readLegacyFlag(LEGACY_KEYS.showDebugIndicators) === true) {
        mask |= Feature.DebugIndicators;
    }
    // legacyMode defaulted to true (legacy view); only an explicit opt-out
    // maps to the inverted NewContentDesign bit.
    if (readLegacyFlag(LEGACY_KEYS.legacyMode) === false) {
        mask |= Feature.NewContentDesign;
    }
    return mask;
}

function readStoredMask(brukerKey: string): number | null {
    const raw = getItem(featureFlagsKey(brukerKey));
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isInteger(parsed) ? parsed : null;
}

function readEffectiveMask(brukerKey: string | undefined): number {
    if (brukerKey === undefined) return 0;
    return (
        (readStoredMask(brukerKey) ?? deriveMaskFromLegacyFlags()) &
        ALL_FEATURES_MASK
    );
}

function writeMask(brukerKey: string, mask: number): void {
    try {
        window.localStorage.setItem(
            featureFlagsKey(brukerKey),
            String(mask & ALL_FEATURES_MASK)
        );
    } catch {
        return;
    }
    window.dispatchEvent(new CustomEvent(FEATURE_FLAGS_EVENT));
}

/** Same-tab changes via the custom event, cross-tab via the storage event. */
function subscribeToFeatureFlags(onChange: () => void): () => void {
    window.addEventListener(FEATURE_FLAGS_EVENT, onChange);
    window.addEventListener("storage", onChange);
    return () => {
        window.removeEventListener(FEATURE_FLAGS_EVENT, onChange);
        window.removeEventListener("storage", onChange);
    };
}

/**
 * Persist the legacy-derived mask under the per-user key and delete the
 * legacy keys. Idempotent — a stored value means migration already ran.
 * Legacy keys carry no user identity, so the value is attributed to
 * whichever account is logged in when this first runs.
 */
export function migrateLegacyFlags(brukerKey: string): void {
    if (readStoredMask(brukerKey) === null) {
        writeMask(brukerKey, deriveMaskFromLegacyFlags());
    }
    try {
        Object.values(LEGACY_KEYS).forEach((key) =>
            window.localStorage.removeItem(key)
        );
    } catch {
        // Storage blocked — nothing to clean up.
    }
}

function useStoredMask(brukerKey: string | undefined): number {
    const getSnapshot = useCallback(
        () => readEffectiveMask(brukerKey),
        [brukerKey]
    );
    return useSyncExternalStore(subscribeToFeatureFlags, getSnapshot);
}

/**
 * The central gating decision: a feature is enabled only when the current
 * user's role allows it AND its bit is set in the user's stored mask. While
 * user state resolves (or when it never does, e.g. beneath the loop-blocked
 * overlay), every feature reports disabled — a feature that isn't confirmed
 * enabled loads nothing.
 */
export function useFeature(feature: Feature): {
    enabled: boolean;
    isLoading: boolean;
} {
    const {data, isLoading} = bffApi.endpoints.getUser.useQuery();
    const mask = useStoredMask(data?.brukerKey);
    const enabled =
        data !== undefined &&
        FEATURE_ROLES[feature].includes(data.rolleId) &&
        (mask & feature) === feature;
    return {enabled, isLoading};
}

/** Pure permission check, no flag involved (e.g. settings-page sections). */
export function useHasRole(roles: readonly number[]): {
    hasRole: boolean;
    isLoading: boolean;
} {
    const {data, isLoading} = bffApi.endpoints.getUser.useQuery();
    return {
        hasRole: data !== undefined && roles.includes(data.rolleId),
        isLoading,
    };
}

/** Whole-mask read/write + per-bundle authorization, for the settings page. */
export function useFeatureMask(): {
    mask: number;
    setMask: (next: number) => void;
    canToggle: (bundleMask: number) => boolean;
    isLoading: boolean;
} {
    const {data, isLoading} = bffApi.endpoints.getUser.useQuery();
    const brukerKey = data?.brukerKey;
    const mask = useStoredMask(brukerKey);

    const setMask = useCallback(
        (next: number) => {
            if (brukerKey === undefined) return;
            writeMask(brukerKey, next);
        },
        [brukerKey]
    );

    const canToggle = useCallback(
        (bundleMask: number) =>
            data !== undefined &&
            ALL_FEATURES.filter((bit) => (bundleMask & bit) === bit).every(
                (bit) => FEATURE_ROLES[bit].includes(data.rolleId)
            ),
        [data]
    );

    return {mask, setMask, canToggle, isLoading};
}
